import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import {
  BarChart3,
  Users,
  DollarSign,
  Clock,
  Scale,
  Layers,
  Wrench,
  ArrowLeft,
  Eye,
  TrendingUp,
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, sub, color = 'primary' }) => (
  <div className="bg-bg-light p-5 rounded-xl border border-text-muted/15" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-10 h-10 rounded-lg bg-${color}/15 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
      <p className="text-sm text-text-secondary">{label}</p>
    </div>
    <p className="text-2xl font-bold text-text-primary">{value}</p>
    {sub && <p className="text-xs text-text-muted mt-1">{sub}</p>}
  </div>
);

const PlatformROI = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchROI();
  }, [user, navigate]);

  const fetchROI = async () => {
    try {
      const res = await api.get('/admin/platform-roi');
      setData(res.data);
    } catch (err) {
      console.error('Failed to load ROI:', err);
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'admin') return null;
  if (loading) return <div className="text-center py-12 text-text-muted">Loading platform metrics...</div>;
  if (!data) return <div className="text-center py-12 text-red-400">Failed to load metrics.</div>;

  const { users: userStats, usage_this_month: usage, roi_all_time: roi, totals } = data;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="platform-roi">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/dashboard/admin')}
          className="p-2 hover:bg-bg-light rounded-lg transition"
          data-testid="back-to-admin-btn"
        >
          <ArrowLeft className="w-5 h-5 text-text-muted" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" />
            Platform Overview
          </h1>
          <p className="text-sm text-text-secondary mt-1">Real-time platform metrics & ROI tracking</p>
        </div>
      </div>

      {/* User Breakdown */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-400" />
          Users
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total Users" value={userStats.total} color="blue-400" />
          <StatCard icon={Users} label="Free" value={userStats.by_plan.free || 0} sub="$0/mo" color="gray-400" />
          <StatCard icon={TrendingUp} label="Pro" value={userStats.by_plan.pro || 0} sub="$14/mo each" color="blue-400" />
          <StatCard icon={TrendingUp} label="Studio" value={userStats.by_plan.studio || 0} sub="$29/mo each" color="purple-400" />
        </div>
        {/* MRR estimate */}
        <div className="mt-3 p-4 bg-bg-light rounded-xl border border-green-500/20">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-secondary">Estimated MRR</span>
            <span className="text-xl font-bold text-green-400" data-testid="mrr-value">
              ${((userStats.by_plan.pro || 0) * 14 + (userStats.by_plan.studio || 0) * 29).toLocaleString()}
            </span>
          </div>
          <p className="text-xs text-text-muted mt-1">
            ({userStats.by_plan.pro || 0} Pro x $14) + ({userStats.by_plan.studio || 0} Studio x $29)
          </p>
        </div>
      </div>

      {/* Usage This Month */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-accent" />
          Usage This Month ({usage.period})
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Eye} label="STL Analyses" value={usage.stl_analyses} color="blue-400" />
          <StatCard icon={Wrench} label="STL Repairs" value={usage.stl_repairs} color="orange-400" />
          <StatCard icon={Layers} label="Support Gens" value={usage.smart_supports} color="green-400" />
          <StatCard icon={Eye} label="Image Diagnoses" value={usage.image_analyses} color="purple-400" />
        </div>
      </div>

      {/* All-Time ROI */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-400" />
          Platform ROI (All-Time)
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Layers}
            label="Total Generations"
            value={totals.support_generations}
            color="primary"
          />
          <StatCard
            icon={Scale}
            label="Material Saved"
            value={`${(roi.material_saved_grams / 1000).toFixed(1)} kg`}
            sub={`${roi.material_saved_grams.toFixed(0)}g total`}
            color="green-400"
          />
          <StatCard
            icon={DollarSign}
            label="Money Saved (Users)"
            value={`$${roi.money_saved_usd.toFixed(2)}`}
            color="green-400"
          />
          <StatCard
            icon={Clock}
            label="Time Saved"
            value={`${(roi.time_saved_minutes / 60).toFixed(1)} hrs`}
            sub={`${roi.time_saved_minutes.toFixed(0)} minutes`}
            color="blue-400"
          />
        </div>
      </div>

      {/* All-Time Totals */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary mb-3">All-Time Totals</h2>
        <div className="grid grid-cols-2 gap-4">
          <StatCard icon={Layers} label="Support Generations" value={totals.support_generations} color="primary" />
          <StatCard icon={Wrench} label="STL Repairs" value={totals.stl_repairs} color="orange-400" />
        </div>
      </div>
    </div>
  );
};

export default PlatformROI;
